/**
 * **用选中节点建组** + **框 / 拖拽 / 尺寸** —— 从 Playground.vue 抽出来。
 *
 * ## 这个文件负责什么
 *
 * 建组（Blender 的 Ctrl+G 那一套）+ 框（frame）的拖拽与尺寸：
 *
 *   `groupSelection`        把选中的节点收进一个组
 *   `edgeKeyOf`             边的身份（两端 + handle）
 *   `wrapSelectionInFrame`  用框把选中的节点圈起来
 *   `assignFrameOf`         拖动节点时把它挂到（或脱离）框
 *   `frameDrag`             一次框拖拽的成员快照
 *   `onNodeDragStart|Drag|DragStop`（`onBoxResize` 留在宿主，跟 useNodeEditing 一块走）
 *
 * ★ 建组时**跨边界的边会变成组的端口**。不这么做的话，建完组外面的线全断了 ——
 * 用户得手动重接一遍，那这个功能就没人用了。
 *
 * ## ⚠️ 抽出来的方式：函数体逐字不动
 *
 * `nodeBoxOf` / `nodePosition` / `looseNode` / `frameBoxes` / `childrenOfFrame`
 * 这几个**到处都在用**（别处也调），所以刻意留在宿主，由 `ctx` 注入。
 */
import { nextTick } from 'vue'
/* ⚠️ 结构类型，不是引擎的类型 —— 换引擎时这一行不用改（`flowTypes.ts`） */
import type { FlowEdge as Edge, FlowNode as Node } from '@/graph/flowTypes'
import {
  GROUP_INPUT_NODE,
  GROUP_OUTPUT_NODE,
  groupRowsFor,
  groupableIds,
  planGrouping,
} from '@/node/group'
import type { GraphNodeModel, SocketDef, SubGraphEdge } from '@/types/node'
import { boundsFor, frameFor, moveMembers } from '@/graph/frame'
import type { Box } from '@/graph/frame'
import { fillMissingPositions } from '@/node/subgraph'
import { planUngroup } from '@/node/group'
import type { GestureState } from '@/graph/canvasGesture'
import type { FlowStoreApi } from '@/graph/flowStore'

export interface GroupingCtx {
  nodes: { value: Node[] }
  edges: { value: Edge[] }
  flowStore: { value: FlowStoreApi | null }
  graphRef: { value: HTMLElement | null }
  /** 正在拖节点（来自 useSubgraphLevels，投影要跳过） */
  draggingNodes: { value: boolean }
  /** 手势状态（拖节点时要把它切回 idle） */
  gesture: { value: GestureState }
  selectedIds: { value: string[] }
  edgeViews: () => SubGraphEdge[]
  modelOf: (id: string) => GraphNodeModel | undefined
  findSocket: (nodeId: string, handleId: string | null | undefined) => SocketDef | undefined
  /** 下面这几个「到处都在用」，故意留在宿主 */
  nodeBoxOf: (id: string) => Box | null
  nodePosition: (id: string) => { x: number; y: number } | null
  looseNode: (id: string) => { id: string; position: { x: number; y: number } } | undefined
  /**
   * 把一批节点的新位置**写进文档**（唯一的写入口，见 `graph/documentSync.ts`）。
   *
   * ⚠️ 拖框时必须走这里，不能 `looseNode(id).position = …` 就地改。
   * 就地改只改到**当前窗口 store 里那一份对象**：宿主的文档没有变化，
   * 于是下一帧「框本身的位置变更」发布文档时，会把成员位置按**旧值**盖回去 ——
   * 表现就是拖框时子节点**来回跳**，也就是用户说的「卡顿感」。
   */
  applyNodePositions: (moves: { id: string; x: number; y: number }[]) => void
  frameBoxes: () => Box[]
  childrenOfFrame: (frameId: string) => string[]
  nextNodeId: (type: string) => string
  makeModelOfType: (type: string, id: string) => GraphNodeModel | null
  makeFlowNode: (model: GraphNodeModel, position: { x: number; y: number }) => Node
  makeEdge: (source: string, sourceHandle: string, target: string, targetHandle: string) => Edge
  commit: (label: string, coalesceMs?: number) => void
  showNotice: (message: string) => void
}

export function useGrouping(ctx: GroupingCtx) {
  const {
    nodes,
    edges,
    flowStore,
    graphRef,
    draggingNodes,
    gesture,
    selectedIds,
    edgeViews,
    modelOf,
    findSocket,
    nodeBoxOf,
    nodePosition,
    looseNode,
    applyNodePositions,
    frameBoxes,
    childrenOfFrame,
    nextNodeId,
    makeModelOfType,
    makeFlowNode,
    makeEdge,
    commit,
    showNotice,
  } = ctx

/* ── 用选中节点建组 ─────────────────────────────────────────── */

/**
 * 把选中的节点收进一个**组**。
 *
 * ★ 跨边界的边会**变成组的端口**。不这么做的话，建完组外面的线全断了 ——
 * 用户得手动重接一遍，那这个功能就没人用了。
 *
 * 组是一次性封装（不是可复用的类型），所以内部节点是直接从选区搬进去的。
 */
function groupSelection() {
  /*
   * ⚠️ 哪些能被收进组是一**条规则**，抽在 `node/group.ts` 的 `groupableIds` 里
   * （纯函数、有单测）。原来它内联在这里，而且**顺手把组也排除了** ——
   * 于是"选中两个组建组"会得到"先选中要收进组的节点"（真机反馈）。
   * 组的嵌套本身是支持的（子图层级、序列化都是递归的），
   * 那条限制说不清理由，所以拿掉了。
   */
  const ids = groupableIds(
    selectedIds.value.flatMap((id) => {
      const model = modelOf(id)
      return model ? [model] : []
    }),
    selectedIds.value,
  )
  if (ids.length === 0) {
    showNotice('先选中要收进组的节点')
    return
  }

  const plan = planGrouping({
    selected: ids,
    edges: edgeViews() as unknown as SubGraphEdge[],
    modelOf: (id) => modelOf(id),
    socketOf: (nodeId, handleId) => {
      const def = findSocket(nodeId, handleId)
      if (!def) return undefined
      // 名字取那一行的 label（用户更认它）
      const row = modelOf(nodeId)?.rows.find(
        (r) => r.kind === 'input' || r.kind === 'output'
          ? r.socket.id === handleId
          : false,
      )
      const label =
        row && (row.kind === 'input' || row.kind === 'output') ? row.label : undefined
      return { type: def.type, label }
    },
  })

  const groupId = nextNodeId('group')
  const groupModel = makeModelOfType('group', groupId)
  if (!groupModel) return

  commit('建组')

  // 组落在选区的左上角
  const boxes = ids.map(nodeBoxOf).filter((b): b is Box => b !== null)
  const origin = boxes.length
    ? {
        x: Math.min(...boxes.map((b) => b.x)),
        y: Math.min(...boxes.map((b) => b.y)),
      }
    : { x: 0, y: 0 }

  groupModel.rows = groupRowsFor(plan.interface.inputs.length, plan.interface.outputs.length)
  groupModel.graph = {
    nodes: plan.innerNodes,
    edges: plan.innerEdges,
    // 内层位置：相对组左上角，这样进组编辑时布局是合理的
    positions: Object.fromEntries(
      ids.flatMap((id) => {
        const position = nodePosition(id)
        return position
          ? [[id, { x: position.x - origin.x, y: position.y - origin.y }]]
          : []
      }),
    ),
  }
  /*
   * ★ 两个接口节点**必须**有位置。
   * 不补的话它们都落在 (0,0)，跟选区里第一个节点叠在一起 ——
   * 进去看到的是一个压着一个的节点。
   * fillMissingPositions 会把它们放到已有内容的左右两侧。
   */
  fillMissingPositions(groupModel.graph, {
    [GROUP_INPUT_NODE]: { x: -1, y: 0 },
    [GROUP_OUTPUT_NODE]: { x: 1, y: 0 },
  })

  type Loose = Record<string, unknown>
  const drop = new Set(ids)
  // 被吃掉的边换成新的外层边（指向组节点）
  const consumed = new Set(plan.consumed.map((e) => edgeKeyOf(e)))

  const keptNodes = (nodes.value as unknown as Loose[]).filter(
    (node) => !drop.has(String(node.id)),
  )
  const keptEdges = (edges.value as unknown as (SubGraphEdge & { id?: string })[]).filter(
    (edge) => !consumed.has(edgeKeyOf(edge)),
  )

  nodes.value = [
    ...keptNodes,
    makeFlowNode(groupModel, origin) as unknown as Loose,
  ] as unknown as Node[]

  edges.value = [
    ...keptEdges,
    ...plan.outerEdges.map((edge) =>
      makeEdge(
        edge.source || groupId,
        edge.sourceHandle ?? '',
        edge.target || groupId,
        edge.targetHandle ?? '',
      ),
    ),
  ] as unknown as Edge[]

  nextTick(() => flowStore.value?.updateNodeInternals())
  showNotice(
    `已成组：${ids.length} 个节点，${plan.interface.inputs.length} 个输入 / ${plan.interface.outputs.length} 个输出端口`,
  )
}

/**
 * ★ **解组**：把组的内部摊回父图，跨越边界的线就地重接。
 *
 * 和 `groupSelection` 互为逆运算（`planUngroup` 的注释里有语义表）。
 *
 * ⚠️ 三件容易漏的事，都在这儿处理：
 *   1. **id 撞名** —— 内层 id 只在那一层唯一，摊上来要改名（纯函数里做）；
 *   2. **位置** —— 内层坐标是**相对组**的，要加上组自己的位置；
 *   3. **悬空边** —— 摊完要检查一遍：接到不存在节点上的边必须丢掉，
 *      否则图引擎会拿 `isValidConnection` 复检并**静默丢线**。
 */
function ungroupSelection() {
  const ids = selectedIds.value.filter((id) => modelOf(id)?.type === 'group')
  if (ids.length === 0) {
    showNotice('先选中要拆开的组')
    return
  }

  const id = ids[0]!
  const model = modelOf(id)
  const graph = model?.graph
  if (!model || !graph) {
    showNotice('这个组没有内部子图')
    return
  }

  const origin = nodePosition(id) ?? { x: 0, y: 0 }
  const parentIds = (nodes.value as unknown as { id: string }[]).map((n) => String(n.id))
  const outer = edgeViews() as unknown as SubGraphEdge[]

  const plan = planUngroup({ graph: model.graph!, groupId: id, parentIds, outerEdges: outer })

  commit('解组')

  /*
   * 内层坐标是**相对组**的（建组时减掉了原点），所以这里加回来 ——
   * 不加的话拆出来的节点会全部挤到画布左上角。
   */
  const placed = plan.nodes.map((inner) => {
    const rel = graph.positions?.[inner.id.replace(new RegExp(`^${id}/`), '')] ?? { x: 0, y: 0 }
    return makeFlowNode(inner, { x: origin.x + rel.x, y: origin.y + rel.y })
  })

  /* 组的边全部作废：跨越的那些已经由 `plan.edges` 重接好了 */
  const surviving = outer.filter((edge) => edge.source !== id && edge.target !== id)

  const known = new Set([...parentIds.filter((n) => n !== id), ...plan.nodes.map((n) => n.id)])
  const bridged = plan.edges
    .filter((e) => known.has(e.source) && known.has(e.target))
    .map((e) => makeEdge(e.source, e.sourceHandle, e.target, e.targetHandle))

  nodes.value = [
    ...(nodes.value as unknown as Record<string, unknown>[]).filter(
      (node) => String(node.id) !== id,
    ),
    ...placed,
  ] as unknown as Node[]

  edges.value = [
    ...(surviving as unknown as Edge[]),
    ...(bridged as unknown as Edge[]),
  ]

  nextTick(() => flowStore.value?.updateNodeInternals())
  showNotice(
    plan.empty
      ? '已解组：这个组里没有节点'
      : `已解组：摊出 ${plan.nodes.length} 个节点，重接 ${bridged.length} 根线`,
  )
}

/** 边的身份：用两端 + handle 拼，跟 makeEdge 保持一致 */
function edgeKeyOf(edge: SubGraphEdge): string {
  return `${edge.source}.${edge.sourceHandle ?? ''}->${edge.target}.${edge.targetHandle ?? ''}`
}

/** 用框把选中的节点圈起来 */
function wrapSelectionInFrame() {
  // 框自己不参与「被框住」
  const ids = selectedIds.value.filter((id) => modelOf(id)?.render !== 'frame')
  if (ids.length === 0) {
    showNotice('先选中要圈住的节点')
    return
  }

  const boxes = ids.map(nodeBoxOf).filter((b): b is Box => b !== null)
  const rect = boundsFor(boxes)
  if (!rect) return

  const id = nextNodeId('frame')
  const model = makeModelOfType('frame', id)
  if (!model) return

  commit('用框圈住节点')
  model.width = rect.width
  model.height = rect.height

  /*
   * ★ 框必须排在数组**前面**：图引擎按数组顺序渲染，排后面的话
   * 框会盖在节点上面，把里面的节点全遮住。
   */
  nodes.value = [
    makeFlowNode(model, { x: rect.x, y: rect.y }) as unknown as Record<string, unknown>,
    ...(nodes.value as unknown as Record<string, unknown>[]),
  ] as unknown as Node[]

  for (const childId of ids) {
    const child = modelOf(childId)
    if (child) child.frameId = id
  }

  nextTick(() => flowStore.value?.updateNodeInternals([...ids, id]))
  showNotice(`已用框圈住 ${ids.length} 个节点`)
}

/**
 * 拖放结束后重新判定归属。
 *
 * 只在**这一刻**判：每次渲染都按几何重算的话，
 * 节点跨在边界上会来回抖，而且挪一下框里面的节点就「掉出去」了。
 */
function assignFrameOf(nodeId: string) {
  const model = modelOf(nodeId)
  if (!model || model.render === 'frame') return

  const box = nodeBoxOf(nodeId)
  if (!box) return

  const next = frameFor(box, frameBoxes()) ?? undefined
  if (model.frameId === next) return
  model.frameId = next

  const frame = next ? modelOf(next) : undefined
  if (frame) showNotice(`已放进框「${frame.label ?? frame.title}」`)
}

/** 拖框时成员的起点快照 —— 用「起点 + 总位移」，不累加增量 */
let frameDrag: {
  frameId: string
  start: { x: number; y: number }
  members: { id: string; x: number; y: number }[]
} | null = null

function onNodeDragStart(params: { node: { id: string } }) {
  commit('移动节点')
  draggingNodes.value = true
  frameDrag = null
  if (modelOf(params.node.id)?.render !== 'frame') return

  const start = nodePosition(params.node.id)
  if (!start) return

  frameDrag = {
    frameId: params.node.id,
    start,
    members: childrenOfFrame(params.node.id).flatMap((id) => {
      const position = nodePosition(id)
      return position ? [{ id, x: position.x, y: position.y }] : []
    }),
  }
}

function onNodeDrag(params: { node: { id: string } }) {
  if (!frameDrag || frameDrag.frameId !== params.node.id) return
  const now = nodePosition(params.node.id)
  if (!now) return

  // ★ 起点快照 + 总位移 —— 累加增量会让误差一步步累积（见 graph/frame.ts）
  const moved = moveMembers(frameDrag.members, {
    x: now.x - frameDrag.start.x,
    y: now.y - frameDrag.start.y,
  })
  /*
   * ⚠️ 走宿主的写入口，**不要**在这里 `node.position = …` 就地改。
   * 就地改只改到当前窗口 store 里那一份；下一帧框自己的位置变更会把
   * 文档重新发布一遍，成员位置随即被旧值盖回去 —— 拖框时子节点来回跳。
   */
  applyNodePositions(moved)
}

function onNodeDragStop(params: { node: { id: string } }) {
  frameDrag = null
  assignFrameOf(params.node.id)
  // 松手：恢复重算（下一次读 evalResult 就会真的算一遍）
  draggingNodes.value = false
}


  return {
    groupSelection,
    ungroupSelection,
    wrapSelectionInFrame,
    assignFrameOf,
    onNodeDragStart,
    onNodeDrag,
    onNodeDragStop,
  }
}
